import { useCallback, useEffect, useState } from "react";

import { useRoutesContext } from "../components/Routes/RoutesContext";
import { useNonModalLocation } from "../components/Utils/routing";

export const isPath = url => {
  return !url.match(/^https?:\/\//);
};

export const isExternalUrl = url => {
  // If not host is included, we have a path so it's not external
  if (isPath(url)) {
    return false;
  }

  const targetDomain = url.split("/")[2];
  const currentDomain = window.location.href.split("/")[2];

  return targetDomain !== currentDomain;
};

// Gets an URL and a paramsObject object hash and adds the key/value pairs contained in
// the paramsObject argument to the URL with the appropriate collator.
//
// Examples :
//
//   urlWithParams("https://example.org", { foo: "bar" })
//   # => "https://example.org?foo=bar"
//
//   urlWithParams("https://example.org?bar=baz", { foo: "bar" })
//   # => "https://example.org?bar=baz&foo=bar"
//
export const urlWithParams = (url, paramsObject) => {
  const params = new URLSearchParams(paramsObject).toString();
  const collator = url.indexOf("?") === -1 ? "?" : "&";
  return [url, params].join(collator);
};

export const useCurrentPathMatchesUrl = url => {
  const { pathname: currentPath } = useNonModalLocation();
  const isSameOrChildUrl = useIsSameOrChildUrl();

  const getIsActiveUrl = useCallback(
    url => isSameOrChildUrl(url, currentPath),
    [currentPath, isSameOrChildUrl]
  );
  const [isActive, setIsActive] = useState(getIsActiveUrl());

  useEffect(() => {
    const newIsActive = getIsActiveUrl(url);
    if (newIsActive === isActive) return;

    setIsActive(newIsActive);
  }, [currentPath, getIsActiveUrl, isActive, url]);

  return isActive;
};

const isChildRouteOf = (routes, childRoute, parentRoute) => {
  if (!parentRoute.childRoutesKeys) return false;

  const { params: parentRouteParams = {} } = parentRoute;
  const { params: childRouteParams = {} } = childRoute;

  // This allows checking whether 2 given URL routes resolve with different params. In
  // most cases, this allows checking whether the slugs contained in the 2 compared URLs
  // are the same or not, as a route with a different slug than the compared parent route
  // can't be a child of the latter.
  const hasDifferentParamValues = Object.keys(parentRoute.params).some(
    paramKey => parentRouteParams[paramKey] !== childRouteParams[paramKey]
  );

  if (hasDifferentParamValues) return false;

  return parentRoute.childRoutesKeys.some(childRoutesKey => {
    const childRoutes = Object.values(routes[childRoutesKey]);

    return childRoutes.some(({ path }) => path === childRoute.path);
  });
};

export const useIsSameOrChildUrl = () => {
  const { resolve, routes } = useRoutesContext();

  return useCallback(
    (firstUrl, secondUrl) => {
      if (!firstUrl || !secondUrl) return false;

      // Normalize to pathnames for comparison
      firstUrl = isPath(firstUrl) ? firstUrl : new URL(firstUrl).pathname;
      secondUrl = isPath(secondUrl) ? secondUrl : new URL(secondUrl).pathname;

      if (firstUrl === secondUrl) return true;
      if (!firstUrl || !secondUrl) return false;

      const firstRoute = resolve(firstUrl);
      const secondRoute = resolve(secondUrl);

      if (!firstRoute || !secondRoute) return false;

      return (
        isChildRouteOf(routes, firstRoute, secondRoute) ||
        isChildRouteOf(routes, secondRoute, firstRoute)
      );
    },
    [resolve, routes]
  );
};
